import { APP_CONFIG } from '@/constants/app';
import {
  ApiResponse,
  AuthResponse,
  ForgotPasswordData,
  LoginCredentials,
  RegisterData,
  RegisterFromInvitationData,
  RegisterFromInvitationResponse,
  ResetPasswordData,
  TwoFactorDisableData,
  TwoFactorSetupResponse,
  TwoFactorVerifyData,
  User,
} from '@/types/auth.types';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';

// Create axios instance for auth service
const authApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(authApi);

export const authService = {
  login: async (
    credentials: LoginCredentials
  ): Promise<ApiResponse<AuthResponse & { requires2fa?: boolean; requiresOtp?: boolean; sessionId?: string }>> => {
    const response = await authApi.post<ApiResponse<AuthResponse & { requires2fa?: boolean; requiresOtp?: boolean; sessionId?: string }>>(
      '/auth/login',
      credentials
    );

    // Save tokens after successful login
    if (response.data.data.tokens) {
      tokenManager.saveTokens(response.data.data.tokens);
    }

    return response.data;
  },

  register: async (data: RegisterData): Promise<ApiResponse<AuthResponse>> => {
    // remove acceptTerms from data
    data['acceptTerms'] = undefined;

    const response = await authApi.post<ApiResponse<AuthResponse>>(
      '/auth/register',
      data
    );

    // Save tokens after successful registration
    if (response.data.data.tokens) {
      tokenManager.saveTokens(response.data.data.tokens);
    }

    return response.data;
  },

  /**
   * Register from vault invitation (for employees)
   * No OpenPaye credentials required
   */
  registerFromInvitation: async (
    data: RegisterFromInvitationData
  ): Promise<ApiResponse<RegisterFromInvitationResponse>> => {
    const response = await authApi.post<
      ApiResponse<RegisterFromInvitationResponse>
    >('/auth/register/invitation', data);

    // Save tokens after successful registration
    if (response.data.data.tokens) {
      tokenManager.saveTokens(response.data.data.tokens);
    }

    return response.data;
  },

  forgotPassword: async (
    data: ForgotPasswordData
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await authApi.post<ApiResponse<{ message: string }>>(
      '/auth/forgot-password',
      data
    );
    return response.data;
  },

  resetPassword: async (
    data: ResetPasswordData
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await authApi.post<ApiResponse<{ message: string }>>(
      '/auth/reset-password',
      data
    );
    return response.data;
  },

  refreshToken: async (
    refreshToken: string
  ): Promise<ApiResponse<AuthResponse>> => {
    const response = await authApi.post<ApiResponse<AuthResponse>>(
      '/auth/refresh',
      { refreshToken }
    );

    // Update stored tokens
    if (response.data.data.tokens) {
      tokenManager.saveTokens(response.data.data.tokens);
    }

    return response.data;
  },

  logout: async (): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response =
        await authApi.post<ApiResponse<{ message: string }>>('/auth/logout');
      return response.data;
    } finally {
      // Always clear tokens on logout, even if API call fails
      tokenManager.clearTokens();
    }
  },

  getProfile: async (): Promise<ApiResponse<User>> => {
    const response = await authApi.get<ApiResponse<User>>('/users/profile');
    return response.data;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    const tokens = tokenManager.getTokens();
    return !!tokens?.accessToken;
  },

  /**
   * Get current access token
   */
  getAccessToken: (): string | null => {
    const tokens = tokenManager.getTokens();
    return tokens?.accessToken || null;
  },

  /**
   * Clear all authentication data
   */
  clearAuth: (): void => {
    tokenManager.clearTokens();
  },

  /**
   * Verify user password (for sensitive operations)
   */
  verifyPassword: async (
    password: string
  ): Promise<ApiResponse<{ isValid: boolean }>> => {
    const response = await authApi.post<ApiResponse<{ isValid: boolean }>>(
      '/auth/verify-password',
      { password }
    );
    return response.data;
  },

  // ===== 2FA Methods =====

  /**
   * Setup 2FA - Generate QR code
   */
  setupTwoFactor: async (): Promise<ApiResponse<TwoFactorSetupResponse>> => {
    const response =
      await authApi.post<ApiResponse<TwoFactorSetupResponse>>(
        '/auth/2fa/setup'
      );
    return response.data;
  },

  /**
   * Enable 2FA after verifying token
   */
  enableTwoFactor: async (
    data: TwoFactorVerifyData
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await authApi.post<ApiResponse<{ message: string }>>(
      '/auth/2fa/enable',
      data
    );
    return response.data;
  },

  /**
   * Verify 2FA token during login
   */
  verifyTwoFactor: async (
    data: TwoFactorVerifyData
  ): Promise<ApiResponse<AuthResponse>> => {
    const response = await authApi.post<ApiResponse<AuthResponse>>(
      '/auth/2fa/verify',
      {
        code: data.token,
        sessionId: data.tempToken,
      }
    );

    // Save tokens after successful 2FA verification
    if (response.data.data.tokens) {
      tokenManager.saveTokens(response.data.data.tokens);
    }

    return response.data;
  },

  /**
   * Disable 2FA with password confirmation
   */
  disableTwoFactor: async (
    data: TwoFactorDisableData
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await authApi.post<ApiResponse<{ message: string }>>(
      '/auth/2fa/disable',
      data
    );
    return response.data;
  },

  /**
   * Check if 2FA setup is pending (for vault users)
   */
  checkPending2FA: async (): Promise<
    ApiResponse<{ requires2FASetup: boolean; twoFactorEnabled: boolean }>
  > => {
    const response = await authApi.get<
      ApiResponse<{ requires2FASetup: boolean; twoFactorEnabled: boolean }>
    >('/auth/2fa/check-pending');
    return response.data;
  },

  /**
   * Complete 2FA setup (clear the requires2FASetup flag)
   */
  complete2FASetup: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await authApi.post<ApiResponse<{ message: string }>>(
      '/auth/2fa/complete-setup'
    );
    return response.data;
  },

  // ===== OTP Methods =====

  /**
   * Verify OTP code during login
   */
  verifyOtp: async (data: {
    sessionId: string;
    code: string;
  }): Promise<ApiResponse<AuthResponse>> => {
    const response = await authApi.post<ApiResponse<AuthResponse>>(
      '/auth/otp/verify',
      data
    );

    // Save tokens after successful OTP verification
    if (response.data.data.tokens) {
      tokenManager.saveTokens(response.data.data.tokens);
    }

    return response.data;
  },

  /**
   * Resend OTP code
   */
  resendOtp: async (
    sessionId: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await authApi.post<ApiResponse<{ message: string }>>(
      '/auth/otp/resend',
      { sessionId }
    );
    return response.data;
  },

  /**
   * Set security method (totp, otp, or null)
   */
  setSecurityMethod: async (
    method: 'totp' | 'otp' | null
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await authApi.post<ApiResponse<{ message: string }>>(
      '/auth/security-method',
      { method }
    );
    return response.data;
  },
};
